<!--
  ============================================================================
  APP.VUE - COMPONENTE RAIZ DE LA APLICACION
  ============================================================================

  PROPOSITO:
  Este es el componente MAS IMPORTANTE de toda la aplicacion.
  Es el "abuelo" de todos los demas componentes. TODO lo que ves
  en pantalla esta contenido DENTRO de este componente.

  RESPONSABILIDADES:
  1. Proveer el contenedor <v-app> obligatorio de Vuetify
  2. Decidir que layout usar segun la ruta actual
  3. Inicializar la aplicacion cuando se monta

  SISTEMA DE LAYOUTS:
  La aplicacion tiene 2 tipos de layouts:

  1. BLANK LAYOUT (pantalla completa sin decoracion):
     - Se usa para: Login, registro, paginas de error
     - NO tiene sidebar ni header
     - La vista ocupa toda la pantalla
     - Se activa cuando route.meta.layout === 'blank'

  2. MAIN LAYOUT (con sidebar y header):
     - Se usa para: Dashboard, emision, historial, configuracion
     - TIENE sidebar lateral con menu de navegacion
     - TIENE header superior con selector de sucursal
     - Se usa para todas las paginas que requieren autenticacion

  FLUJO DE RENDERIZADO:
  1. Vue ejecuta main.js y monta App.vue en el DOM
  2. App.vue lee la ruta actual (ej: /login o /dashboard)
  3. Si la ruta tiene meta.layout='blank' o es 'login':
     -> Renderiza solo <router-view> (vista desnuda)
  4. Si la ruta NO tiene meta.layout='blank':
     -> Renderiza <MainLayout> que contiene sidebar + header
     -> Dentro de MainLayout hay otro <router-view>

  RELACION CON OTROS ARCHIVOS:
  - main.js: Crea la instancia de Vue y monta App.vue
  - router/index.js: Define las rutas y sus meta datos
  - MainLayout.vue: El layout con sidebar y header
  - Todas las vistas (Dashboard, Login, etc.) se renderizan aqui
  ============================================================================
-->

<template>
  <!--
    =========================================================================
    V-APP: CONTENEDOR OBLIGATORIO DE VUETIFY
    =========================================================================

    <v-app> es ABSOLUTAMENTE NECESARIO para que Vuetify funcione.
    Debe ser el elemento raiz de tu aplicacion Vue + Vuetify.

    QUE HACE <v-app>:
    1. Crea el contexto de Vuetify (tema, breakpoints, etc.)
    2. Maneja el sistema de capas (z-index) para modales y menus
    3. Provee CSS base para todos los componentes Vuetify
    4. Detecta el tamaño de pantalla para responsive design

    SI QUITAS <v-app>:
    - Los modales (v-dialog) no funcionaran bien
    - Los menus (v-menu) aparecen en lugares incorrectos
    - El tema de colores no se aplica
    - Los estilos de Vuetify se rompen

    NOTA: Solo debe haber UN <v-app> en toda la aplicacion.
    =========================================================================
  -->
  <v-app>
    <!-- Skip link para accesibilidad -->
    <a href="#main-content" class="skip-link">Saltar al contenido</a>

    <!--
      =========================================================================
      LOGICA DE SELECCION DE LAYOUT
      =========================================================================

      Aqui decidimos que mostrar basandonos en el tipo de layout.

      OPCION 1: BLANK LAYOUT (v-if="isBlankLayout")
      Cuando isBlankLayout es TRUE, mostramos SOLO el <router-view>.
      Esto significa que la vista (ej: LoginView.vue) ocupa toda la pantalla
      sin ningun sidebar, header o decoracion.

      Esto es perfecto para:
      - Pagina de login (el usuario no esta autenticado)
      - Pagina de registro
      - Paginas de error (404, 500)
      - Cualquier pagina que necesite pantalla completa

      <router-view> es un componente especial de Vue Router que:
      - Lee la URL actual del navegador
      - Busca en la configuracion de rutas que componente corresponde
      - Renderiza ese componente en este lugar

      Ejemplo:
        URL: /login -> router-view renderiza LoginView.vue
        URL: /registro -> router-view renderiza RegistroView.vue
      =========================================================================
    -->
    <router-view v-if="isBlankLayout" v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>

    <!--
      =========================================================================
      OPCION 2: MAIN LAYOUT (v-else)
      =========================================================================

      Cuando isBlankLayout es FALSE, mostramos MainLayout.

      MainLayout es un componente que contiene:
      - AppSidebar: Menu lateral de navegacion
      - AppHeader: Barra superior con selector de sucursal
      - v-main: Area de contenido principal

      Dentro de MainLayout, hay OTRO <router-view> que renderiza
      la vista correspondiente a la ruta actual.

      ESTRUCTURA RESULTANTE:
      <v-app>
        <MainLayout>
          <AppSidebar />      <- Menu lateral
          <AppHeader />       <- Barra superior
          <v-main>
            <router-view />   <- Aqui va Dashboard, Factura, etc.
          </v-main>
        </MainLayout>
      </v-app>

      El <router-view> dentro de MainLayout (linea 8) renderiza vistas como:
        URL: /dashboard -> DashboardView.vue
        URL: /emision/factura -> FacturaView.vue
        URL: /historial -> HistorialView.vue

      NOTA: Usamos un SLOT para pasar el <router-view> a MainLayout.
      El <router-view> de la linea 8 sera insertado donde MainLayout
      tenga un <slot></slot> definido.
      =========================================================================
    -->
    <MainLayout v-else>
      <!-- Overlay de carga inicial -->
      <v-overlay
        :model-value="appLoading"
        persistent
        class="align-center justify-center"
        scrim="transparent"
      >
        <div class="text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
            width="4"
          />
          <p class="text-body-2 mt-3" style="color: rgba(var(--v-theme-on-surface), 0.6)">
            Inicializando sistema...
          </p>
        </div>
      </v-overlay>

      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </MainLayout>

    <!-- Aviso de sesión por expirar -->
    <v-snackbar
      v-model="sesionPorExpirar"
      color="warning"
      :timeout="-1"
      location="top"
    >
      <v-icon start>mdi-clock-alert-outline</v-icon>
      Tu sesión expira en {{ minutosRestantes }} minuto(s). Guarda tu trabajo.
      <template #actions>
        <v-btn variant="text" @click="descartarAviso">Entendido</v-btn>
      </template>
    </v-snackbar>

    <!-- Snackbar global para notificaciones -->
    <v-snackbar
      v-model="globalSnackbar.show"
      :color="globalSnackbar.color"
      :timeout="globalSnackbar.timeout"
      :location="$vuetify.display.xs ? 'bottom' : 'bottom right'"
    >
      {{ globalSnackbar.message }}
      <template #actions>
        <v-btn variant="text" @click="globalSnackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>

    <!-- Dialog global "Sesion expirada" disparado por interceptor 401 -->
    <SessionExpiredDialog />
  </v-app>
</template>

<script>
/**
 * ===========================================================================
 * SECCION SCRIPT - LOGICA DEL COMPONENTE
 * ===========================================================================
 *
 * Aqui definimos la logica JavaScript del componente App.vue.
 * Usamos la COMPOSITION API de Vue 3 (funcion setup()).
 *
 * COMPOSITION API vs OPTIONS API:
 *
 * OPTIONS API (Vue 2 style):
 *   export default {
 *     data() { return { ... } },
 *     computed: { ... },
 *     methods: { ... },
 *     mounted() { ... }
 *   }
 *
 * COMPOSITION API (Vue 3 style):
 *   export default {
 *     setup() {
 *       const dato = ref(...)
 *       const computado = computed(...)
 *       const metodo = () => { ... }
 *       onMounted(() => { ... })
 *       return { dato, computado, metodo }
 *     }
 *   }
 *
 * VENTAJAS DE COMPOSITION API:
 * - Mejor organizacion del codigo por funcionalidad
 * - Mas facil reutilizar logica (composables)
 * - Mejor inferencia de tipos con TypeScript
 * - Codigo mas limpio para componentes complejos
 * ===========================================================================
 */

/**
 * Importaciones de Vue Composition API
 *
 * computed: Crea una propiedad computada (se recalcula automaticamente
 *           cuando sus dependencias cambian). Similar a una formula de Excel.
 *
 * onMounted: Hook de ciclo de vida. La funcion que le pasas se ejecuta
 *            UNA VEZ cuando el componente se monta en el DOM.
 *            Es el equivalente a mounted() de Options API.
 */
import { computed, reactive, onMounted, provide } from 'vue'
import { useSessionWarning } from '@/composables/useSessionWarning'

/**
 * useRoute: Hook de Vue Router para acceder a la ruta actual.
 *
 * Retorna un objeto reactivo con informacion de la ruta:
 * - route.path: La URL (ej: '/dashboard', '/emision/factura')
 * - route.name: El nombre de la ruta (ej: 'dashboard', 'emision-factura')
 * - route.params: Parametros de la URL (ej: { id: '123' })
 * - route.query: Query strings (ej: { page: '2' })
 * - route.meta: Metadata definida en la configuracion de rutas
 *
 * Es REACTIVO, asi que si la URL cambia, todas las propiedades
 * computadas que dependen de route se actualizan automaticamente.
 */
import { useRoute } from 'vue-router'

/**
 * useStore: Hook de Vuex para acceder al store global.
 *
 * Retorna la instancia del store donde puedes:
 * - Leer estado: store.state.modulo.propiedad
 * - Usar getters: store.getters['modulo/getter']
 * - Dispatch actions: store.dispatch('modulo/action', payload)
 * - Commit mutations: store.commit('modulo/MUTATION', payload)
 *
 * El store es donde guardamos datos globales como:
 * - Usuario autenticado
 * - Catalogos del MH
 * - Lista de documentos
 * - Sucursal seleccionada
 */
import { useStore } from 'vuex'

/**
 * MainLayout: Componente del layout principal.
 *
 * Importamos desde @/components/layout/MainLayout.vue
 *
 * El @ es un alias configurado en vue.config.js o webpack que
 * apunta a la carpeta src/. Es lo mismo que escribir:
 * import MainLayout from './components/layout/MainLayout.vue'
 *
 * Usar @ es mas conveniente porque no importa en que carpeta
 * este el archivo actual, siempre puedes referirte a src/.
 *
 * MainLayout contiene:
 * - AppSidebar (menu lateral)
 * - AppHeader (barra superior)
 * - Slot para el contenido (donde va router-view)
 */
import MainLayout from '@/components/layout/MainLayout.vue'
import SessionExpiredDialog from '@/components/auth/SessionExpiredDialog.vue'

export default {
  /**
   * name: Nombre del componente.
   *
   * El nombre es util para:
   * - Debugging en Vue DevTools (veras "App" en el arbol de componentes)
   * - Referenciarse a si mismo en componentes recursivos
   * - Identificar el componente en warnings y errores
   *
   * Por convencion, el componente raiz se llama 'App'.
   */
  name: 'App',

  /**
   * components: Registro de componentes hijos.
   *
   * Para usar un componente en el template, DEBES registrarlo aqui.
   * El nombre de la propiedad es como lo usaras en el template:
   *   MainLayout -> <MainLayout> o <main-layout>
   *
   * Vue automaticamente convierte PascalCase a kebab-case, asi que
   * <MainLayout> y <main-layout> son equivalentes.
   *
   * NOTA: Los componentes de Vuetify (v-app, v-btn, etc.) no necesitan
   * registrarse porque Vuetify los registra globalmente en main.js.
   */
  components: {
    MainLayout,
    SessionExpiredDialog
  },

  /**
   * setup(): El corazon de la Composition API.
   *
   * Esta funcion se ejecuta ANTES de que el componente se monte.
   * Aqui es donde:
   * - Defines variables reactivas (ref, reactive)
   * - Creas propiedades computadas (computed)
   * - Registras hooks de ciclo de vida (onMounted, onUnmounted)
   * - Defines funciones/metodos
   *
   * TODO lo que retornes estara disponible en el template.
   * Si no retornas algo, NO podras usarlo en el template.
   *
   * setup() recibe dos parametros (no los usamos aqui):
   * - props: Las props que recibe el componente
   * - context: Objeto con { attrs, slots, emit, expose }
   */
  setup() {
    /**
     * Obtener la ruta actual.
     *
     * useRoute() retorna un objeto reactivo con la ruta actual.
     * Cada vez que el usuario navega a otra pagina, este objeto
     * se actualiza automaticamente.
     *
     * IMPORTANTE: useRoute() solo puede llamarse dentro de setup()
     * o de un composable. NO funciona fuera de estos contextos.
     *
     * Propiedades utiles de route:
     * - route.path: '/emision/factura'
     * - route.name: 'emision-factura'
     * - route.params: { id: '123' } (parametros dinamicos)
     * - route.query: { page: '1' } (query strings)
     * - route.meta: { requiresAuth: true, layout: 'blank' }
     * - route.matched: Array de todas las rutas que coinciden
     */
    const route = useRoute()

    /**
     * Obtener el store de Vuex.
     *
     * useStore() retorna la instancia del store global.
     * Es equivalente a this.$store en Options API.
     *
     * Con el store podemos:
     * - store.state.auth.user -> Leer el usuario logueado
     * - store.getters['catalogos/formasPago'] -> Usar getters
     * - store.dispatch('auth/login', datos) -> Ejecutar acciones
     * - store.commit('auth/SET_USER', user) -> Commit mutations
     *
     * IMPORTANTE: useStore() solo puede llamarse dentro de setup().
     */
    const store = useStore()

    /**
     * isBlankLayout: Determina si debemos usar el layout en blanco.
     *
     * computed() crea una propiedad computada. Es como una variable
     * que se recalcula AUTOMATICAMENTE cuando sus dependencias cambian.
     *
     * COMO FUNCIONA:
     * 1. Vue analiza la funcion y detecta que depende de route.meta y route.name
     * 2. Cuando route cambia (usuario navega), Vue re-ejecuta la funcion
     * 3. El nuevo valor se propaga a todo lo que use isBlankLayout
     *
     * LOGICA:
     * - Si route.meta.layout === 'blank' -> Es blank layout
     * - Si route.name === 'login' -> Es blank layout (por seguridad)
     * - Cualquier otro caso -> Es main layout
     *
     * DONDE SE DEFINE route.meta.layout:
     * En router/index.js, al definir cada ruta:
     *   {
     *     path: '/login',
     *     name: 'login',
     *     component: LoginView,
     *     meta: { layout: 'blank' }  // <-- Aqui
     *   }
     *
     * EJEMPLO DE USO EN TEMPLATE:
     *   <router-view v-if="isBlankLayout" />  // Se muestra si es true
     *   <MainLayout v-else />                 // Se muestra si es false
     */
    const isBlankLayout = computed(() => {
      // Retorna TRUE si:
      // 1. La ruta tiene meta.layout === 'blank', O
      // 2. El nombre de la ruta es 'login'
      return route.meta.layout === 'blank' || route.name === 'login'
    })

    /**
     * onMounted: Hook de ciclo de vida.
     *
     * La funcion que pasamos se ejecuta UNA VEZ cuando el componente
     * se ha montado en el DOM (es decir, ya es visible en pantalla).
     *
     * CICLO DE VIDA DE UN COMPONENTE:
     * 1. setup() - Se ejecuta primero
     * 2. beforeMount - Antes de insertar en DOM
     * 3. mounted/onMounted - Ya esta en el DOM <-- AQUI
     * 4. beforeUpdate - Antes de re-renderizar
     * 5. updated - Despues de re-renderizar
     * 6. beforeUnmount - Antes de destruir
     * 7. unmounted - Ya destruido
     *
     * USAMOS onMounted PARA:
     * - Inicializar la aplicacion
     * - Cargar datos iniciales
     * - Configurar event listeners
     * - Integrar con librerias de terceros
     *
     * NOTA: Usamos async/await porque initApp es una action asincrona
     * que carga datos del servidor (token guardado, catalogos, etc.)
     */
    onMounted(async () => {
      /**
       * store.dispatch('initApp')
       *
       * Dispara la action 'initApp' definida en store/index.js.
       *
       * Esta action se encarga de:
       * 1. Verificar si hay un token JWT guardado en localStorage
       * 2. Si hay token, validarlo y cargar datos del usuario
       * 3. Cargar catalogos basicos del Ministerio de Hacienda
       * 4. Cargar datos del emisor y sucursales
       *
       * Es ASINCRONA (devuelve una Promise) porque hace peticiones HTTP.
       * Por eso usamos await para esperar a que termine.
       *
       * SI NO HICIERAMOS ESTO:
       * - El usuario tendria que loguearse cada vez que recarga la pagina
       * - Los catalogos no estarian disponibles al abrir una factura
       * - La app mostraria datos incompletos al inicio
       *
       * RELACION CON store/index.js:
       *   // En store/index.js
       *   actions: {
       *     async initApp({ dispatch }) {
       *       await dispatch('auth/checkAuth')
       *       await dispatch('catalogos/fetchCatalogosBasicos')
       *       await dispatch('emisor/fetchEmisor')
       *     }
       *   }
       */
      await store.dispatch('initApp')

      /**
       * Desactivar autocompletado del navegador en dropdowns de Vuetify.
       * El navegador muestra su propia lista de sugerencias que interfiere
       * con los v-select, v-autocomplete y v-combobox.
       */
      const disableAutocomplete = () => {
        const selectors = [
          '.v-select input',
          '.v-autocomplete input',
          '.v-combobox input'
        ]
        document.querySelectorAll(selectors.join(', ')).forEach(input => {
          input.setAttribute('autocomplete', 'off')
          input.setAttribute('autocorrect', 'off')
          input.setAttribute('autocapitalize', 'off')
          input.setAttribute('spellcheck', 'false')
        })
      }

      // Ejecutar inicialmente y observar cambios en el DOM
      disableAutocomplete()

      // MutationObserver para detectar nuevos inputs agregados dinámicamente
      const observer = new MutationObserver(() => {
        disableAutocomplete()
      })

      observer.observe(document.body, {
        childList: true,
        subtree: true
      })
    })

    /**
     * return: Exponer propiedades al template.
     *
     * TODO lo que retornes aqui estara disponible en el <template>.
     * Si no lo retornas, NO podras usarlo en el template.
     *
     * En este caso solo retornamos isBlankLayout porque es lo unico
     * que necesitamos usar en el template (en el v-if).
     *
     * route y store NO los retornamos porque no los usamos
     * directamente en el template, solo en el script.
     */
    const appLoading = computed(() => store.getters.appLoading)

    // Aviso de sesión por expirar
    const { sesionPorExpirar, minutosRestantes, descartarAviso } = useSessionWarning()

    // Snackbar global — accesible desde cualquier componente via $root
    const globalSnackbar = reactive({
      show: false,
      message: '',
      color: 'success',
      timeout: 3000
    })

    // Exponer método global para mostrar notificaciones
    const showNotification = (message, color = 'success', timeout = 3000) => {
      globalSnackbar.message = message
      globalSnackbar.color = color
      globalSnackbar.timeout = timeout
      globalSnackbar.show = true
    }

    // Registrar en provide para que los componentes hijos puedan usarlo
    provide('showNotification', showNotification)

    return {
      isBlankLayout,
      appLoading,
      globalSnackbar,
      sesionPorExpirar,
      minutosRestantes,
      descartarAviso
    }
  }
}
</script>

<style>
/**
 * ===========================================================================
 * SECCION STYLE - ESTILOS GLOBALES
 * ===========================================================================
 *
 * Estilos globales de la aplicación que se aplican a todos los componentes.
 * NO tiene el atributo "scoped" para que afecte a toda la aplicación.
 * ===========================================================================
 */

/* Transiciones de página — slide + fade (playbook 3.1, alineado con SmartHub/
   SmartInventory: entra desde abajo, sale hacia arriba). El bloque de
   prefers-reduced-motion mas abajo ya desactiva estas transiciones. */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* NProgress - barra de carga global */
#nprogress .bar {
  background: rgb(var(--v-theme-primary)) !important;
  height: 3px !important;
}

#nprogress .peg {
  box-shadow: 0 0 10px rgb(var(--v-theme-primary)), 0 0 5px rgb(var(--v-theme-primary)) !important;
}

/* Estilos base para html y body */
html, body {
  height: 100%;
  overflow-x: hidden;
}


/**
 * ===========================================================================
 * ESTILOS PARA TEMA OSCURO - INPUTS Y FORMULARIOS
 * ===========================================================================
 *
 * Los inputs outlined de Vuetify tienen labels con fondo transparente.
 * En tema oscuro, esto causa que los labels se vean tapados.
 * Estos estilos corrigen el problema asegurando que los labels
 * tengan el fondo correcto según el tema activo.
 */

/* ============================================
   FIX LABELS FLOTANTES EN LIGHT MODE
   El label flotante necesita un fondo sólido que tape
   la línea del borde outlined detrás del texto
   ============================================ */

/* Label flotante en light mode - fondo blanco por defecto */
.v-field--variant-outlined .v-field-label--floating {
  background: rgb(var(--v-theme-surface, 255, 255, 255)) !important;
  padding: 0 6px;
  margin-left: -2px;
}

/* En cards (light mode) */
.v-card .v-field--variant-outlined .v-field-label--floating {
  background: rgb(var(--v-theme-surface, 255, 255, 255)) !important;
}

/* En dialogs (light mode) */
.v-dialog .v-field--variant-outlined .v-field-label--floating {
  background: rgb(var(--v-theme-surface, 255, 255, 255)) !important;
}

/* Labels flotantes de inputs outlined en tema oscuro */
.v-theme--dark .v-field--variant-outlined .v-field__outline::before,
.v-theme--darkTheme .v-field--variant-outlined .v-field__outline::before {
  border-color: rgba(255, 255, 255, 0.24) !important;
}

/* Labels de inputs en modo oscuro - texto claro */
.v-theme--dark .v-field-label,
.v-theme--darkTheme .v-field-label {
  color: rgba(255, 255, 255, 0.7) !important;
  background: transparent !important;
}

/* Label flotante por defecto - usa el fondo del background (slate-900) */
.v-theme--dark .v-field--variant-outlined .v-field-label--floating,
.v-theme--darkTheme .v-field--variant-outlined .v-field-label--floating {
  background: #0F172A !important;
  padding: 0 6px;
  margin-left: -2px;
  color: rgba(255, 255, 255, 0.9) !important;
}

/* Label flotante en cards - usa el fondo de surface (slate-800) */
.v-theme--dark .v-card .v-field--variant-outlined .v-field-label--floating,
.v-theme--darkTheme .v-card .v-field--variant-outlined .v-field-label--floating {
  background: #1E293B !important;
}

/* Label flotante en dialogs/modals - usa el fondo de surface (slate-800) */
.v-theme--dark .v-dialog .v-field--variant-outlined .v-field-label--floating,
.v-theme--darkTheme .v-dialog .v-field--variant-outlined .v-field-label--floating {
  background: #1E293B !important;
}

/* Label flotante en sheets/bottom sheets */
.v-theme--dark .v-sheet .v-field--variant-outlined .v-field-label--floating,
.v-theme--darkTheme .v-sheet .v-field--variant-outlined .v-field-label--floating {
  background: #1E293B !important;
}

/* Label flotante en navigation drawers */
.v-theme--dark .v-navigation-drawer .v-field--variant-outlined .v-field-label--floating,
.v-theme--darkTheme .v-navigation-drawer .v-field--variant-outlined .v-field-label--floating {
  background: #1E293B !important;
}

/* ============================================
   FIX PARA EL NOTCH Y OUTLINE DE INPUTS
   El "notch" es el hueco en el borde donde flota el label
   ============================================ */

/* Notch del outline - sin fondo para que no se vea el rectángulo */
.v-theme--dark .v-field__outline__notch,
.v-theme--darkTheme .v-field__outline__notch {
  background: transparent !important;
}

/* Todos los elementos del outline - transparentes */
.v-theme--dark .v-field__outline__start,
.v-theme--dark .v-field__outline__end,
.v-theme--darkTheme .v-field__outline__start,
.v-theme--darkTheme .v-field__outline__end {
  background: transparent !important;
}

/* Overlay del field - transparente en modo oscuro */
.v-theme--dark .v-field__overlay,
.v-theme--darkTheme .v-field__overlay {
  background: transparent !important;
}

/* Hints y mensajes debajo de inputs - texto claro sin fondo */
.v-theme--dark .v-messages__message,
.v-theme--darkTheme .v-messages__message {
  color: rgba(255, 255, 255, 0.6) !important;
}

/* Counter de caracteres */
.v-theme--dark .v-counter,
.v-theme--darkTheme .v-counter {
  color: rgba(255, 255, 255, 0.5) !important;
}

/* Inputs outlined en tema oscuro - sin fondo */
.v-theme--dark .v-field--variant-outlined,
.v-theme--darkTheme .v-field--variant-outlined {
  background: transparent;
}

/* Hover en inputs - tema oscuro */
.v-theme--dark .v-field--variant-outlined:hover .v-field__outline::before,
.v-theme--darkTheme .v-field--variant-outlined:hover .v-field__outline::before {
  border-color: rgba(255, 255, 255, 0.4) !important;
}

/* Cards en tema oscuro - borde sutil para mejor definición */
.v-theme--darkTheme .v-card {
  border: 1px solid rgba(255, 255, 255, 0.08);
}

/* Tablas en tema oscuro */
.v-theme--darkTheme .v-table {
  background: rgb(var(--v-theme-surface)) !important;
}

.v-theme--darkTheme .v-table thead {
  background: rgb(var(--v-theme-surface-variant)) !important;
}

.v-theme--darkTheme .v-data-table__tr:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

/* Dividers en tema oscuro */
.v-theme--darkTheme .v-divider {
  border-color: rgba(255, 255, 255, 0.12) !important;
}

/* ============================================
   FIX PARA VISTA PREVIA DTE (FACTURA) EN DARK MODE
   El documento tenía fondo blanco fijo que lo hacía
   parecer opaco/blanqueado en modo oscuro
   ============================================ */

/* Contenedor principal del documento DTE */
.v-theme--dark .documento-dte,
.v-theme--darkTheme .documento-dte {
  background: rgb(var(--v-theme-surface)) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

/* Tablas del documento DTE */
.v-theme--dark .documento-dte .dte-table,
.v-theme--darkTheme .documento-dte .dte-table {
  background: rgb(var(--v-theme-surface)) !important;
}

.v-theme--dark .documento-dte .dte-table td,
.v-theme--dark .documento-dte .dte-table th,
.v-theme--darkTheme .documento-dte .dte-table td,
.v-theme--darkTheme .documento-dte .dte-table th {
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Celdas de etiquetas (labels) */
.v-theme--dark .documento-dte .label-cell,
.v-theme--darkTheme .documento-dte .label-cell {
  background: rgb(var(--v-theme-surface-variant)) !important;
  color: rgba(255, 255, 255, 0.7) !important;
}

/* Celdas de valores */
.v-theme--dark .documento-dte .value-cell,
.v-theme--darkTheme .documento-dte .value-cell {
  background: rgb(var(--v-theme-surface)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Títulos de sección */
.v-theme--dark .documento-dte .section-title,
.v-theme--darkTheme .documento-dte .section-title {
  background: rgb(var(--v-theme-primary)) !important;
  color: white !important;
}

/* Filas de encabezado */
.v-theme--dark .documento-dte .header-row td,
.v-theme--dark .documento-dte .header-cell,
.v-theme--darkTheme .documento-dte .header-row td,
.v-theme--darkTheme .documento-dte .header-cell {
  background: rgb(var(--v-theme-surface-variant)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Celdas de datos */
.v-theme--dark .documento-dte .data-cell,
.v-theme--darkTheme .documento-dte .data-cell {
  background: rgb(var(--v-theme-surface)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Fila de suma */
.v-theme--dark .documento-dte .suma-row td,
.v-theme--dark .documento-dte .suma-label-cell,
.v-theme--dark .documento-dte .suma-value-cell,
.v-theme--darkTheme .documento-dte .suma-row td,
.v-theme--darkTheme .documento-dte .suma-label-cell,
.v-theme--darkTheme .documento-dte .suma-value-cell {
  background: rgb(var(--v-theme-surface-variant)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Tabla de totales */
.v-theme--dark .documento-dte .totales-table td,
.v-theme--darkTheme .documento-dte .totales-table td {
  background: rgb(var(--v-theme-surface)) !important;
}

.v-theme--dark .documento-dte .totales-label,
.v-theme--darkTheme .documento-dte .totales-label {
  background: rgb(var(--v-theme-surface-variant)) !important;
  color: rgba(255, 255, 255, 0.8) !important;
}

.v-theme--dark .documento-dte .totales-value,
.v-theme--darkTheme .documento-dte .totales-value {
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Tributos - encabezado */
.v-theme--dark .documento-dte .totales-table .tributo-header td,
.v-theme--darkTheme .documento-dte .totales-table .tributo-header td {
  background: rgba(var(--v-theme-primary), 0.3) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Tributos - fila (IVA) - color con buen contraste para texto blanco */
.v-theme--dark .documento-dte .totales-table .tributo-row td,
.v-theme--darkTheme .documento-dte .totales-table .tributo-row td {
  background: #5D4037 !important;
  color: white !important;
}

/* Total final - mantiene verde */
.v-theme--dark .documento-dte .totales-table .total-final td,
.v-theme--darkTheme .documento-dte .totales-table .total-final td {
  background: #2E7D32 !important;
  color: white !important;
}

/* Placeholder QR */
.v-theme--dark .documento-dte .qr-placeholder,
.v-theme--darkTheme .documento-dte .qr-placeholder {
  border-color: rgba(255, 255, 255, 0.3) !important;
  background: rgb(var(--v-theme-surface-variant)) !important;
}

/* Versión móvil */
.v-theme--dark .documento-mobile,
.v-theme--darkTheme .documento-mobile {
  background: rgb(var(--v-theme-surface)) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.v-theme--dark .documento-mobile .dte-section,
.v-theme--darkTheme .documento-mobile .dte-section {
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.v-theme--dark .documento-mobile .section-body,
.v-theme--darkTheme .documento-mobile .section-body {
  background: rgb(var(--v-theme-surface)) !important;
}

.v-theme--dark .documento-mobile .control-item,
.v-theme--dark .documento-mobile .info-item,
.v-theme--darkTheme .documento-mobile .control-item,
.v-theme--darkTheme .documento-mobile .info-item {
  background: rgb(var(--v-theme-surface-variant)) !important;
}

.v-theme--dark .documento-mobile .control-label,
.v-theme--dark .documento-mobile .info-label,
.v-theme--darkTheme .documento-mobile .control-label,
.v-theme--darkTheme .documento-mobile .info-label {
  color: rgba(255, 255, 255, 0.6) !important;
}

.v-theme--dark .documento-mobile .control-value,
.v-theme--dark .documento-mobile .info-value,
.v-theme--darkTheme .documento-mobile .control-value,
.v-theme--darkTheme .documento-mobile .info-value {
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Chips en tema oscuro */
.v-theme--darkTheme .v-chip--variant-tonal {
  background: rgba(255, 255, 255, 0.1) !important;
}

/* Dialogs y menús en tema oscuro */
.v-theme--darkTheme .v-overlay__content > .v-card,
.v-theme--darkTheme .v-menu > .v-overlay__content > .v-card,
.v-theme--darkTheme .v-menu > .v-overlay__content > .v-list {
  background: rgb(var(--v-theme-surface)) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Lists en tema oscuro */
.v-theme--darkTheme .v-list-item:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

/**
 * ===========================================================================
 * ESTILOS PARA TEMA CLARO
 * ===========================================================================
 */

/* El fondo usa la variable del tema para adaptarse automáticamente */
.v-application {
  background-color: rgb(var(--v-theme-background)) !important;
}

/**
 * ===========================================================================
 * ESTILOS ADICIONALES PARA TEMA OSCURO
 * ===========================================================================
 * Mejoras de contraste y opacidad para elementos en modo oscuro
 */

/* Autocomplete y selects - dropdown en modo oscuro */
.v-theme--darkTheme .v-autocomplete__content,
.v-theme--darkTheme .v-select__content {
  background: rgb(var(--v-theme-surface)) !important;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

/* Textarea en modo oscuro */
.v-theme--darkTheme .v-textarea .v-field__input {
  background: transparent;
}

/* Expansion panels en modo oscuro */
.v-theme--darkTheme .v-expansion-panel {
  background: rgb(var(--v-theme-surface)) !important;
}

.v-theme--darkTheme .v-expansion-panel-title {
  background: rgb(var(--v-theme-surface-variant)) !important;
}

/* Stepper en modo oscuro - Visibilidad mejorada */
.v-theme--darkTheme .v-stepper {
  background: rgb(var(--v-theme-surface)) !important;
}

.v-theme--darkTheme .v-stepper-header {
  background: transparent !important;
}

/* Pasos del stepper */
.v-theme--darkTheme .v-stepper-item {
  color: rgb(var(--v-theme-on-surface)) !important;
}

.v-theme--darkTheme .v-stepper-item__avatar {
  background: rgb(var(--v-theme-surface-variant)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
}

.v-theme--darkTheme .v-stepper-item--selected .v-stepper-item__avatar {
  background: rgb(var(--v-theme-primary)) !important;
  color: white !important;
}

.v-theme--darkTheme .v-stepper-item--complete .v-stepper-item__avatar {
  background: rgb(var(--v-theme-success)) !important;
  color: white !important;
}

/* Líneas conectoras del stepper */
.v-theme--darkTheme .v-stepper-item__subtitle,
.v-theme--darkTheme .v-stepper-item__title {
  color: rgb(var(--v-theme-on-surface)) !important;
}

.v-theme--darkTheme .v-stepper-item--selected .v-stepper-item__title {
  color: rgb(var(--v-theme-primary)) !important;
  font-weight: 600;
}

/* Línea divisora entre pasos */
.v-theme--darkTheme .v-stepper-item .v-divider {
  border-color: rgba(255, 255, 255, 0.2) !important;
}

/* Tabs en modo oscuro */
.v-theme--darkTheme .v-tabs {
  background: rgb(var(--v-theme-surface)) !important;
}

/* Alerts en modo oscuro - más visibles */
.v-theme--darkTheme .v-alert--variant-tonal {
  background-color: rgba(var(--v-theme-on-surface), 0.1) !important;
}

/* Skeleton loaders en modo oscuro */
.v-theme--darkTheme .v-skeleton-loader__bone {
  background: rgba(255, 255, 255, 0.1) !important;
}

/* Progress bars en modo oscuro */
.v-theme--darkTheme .v-progress-linear__background {
  background: rgba(255, 255, 255, 0.12) !important;
}

/* Tooltips en modo oscuro */
.v-theme--darkTheme .v-tooltip > .v-overlay__content {
  background: rgb(var(--v-theme-surface-variant)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Snackbars en modo oscuro */
.v-theme--darkTheme .v-snackbar__wrapper {
  background: #334155 !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Navegación drawer y app bar ya tienen sus estilos en sus componentes */

/* Fix para v-select items en modo oscuro */
.v-theme--darkTheme .v-list-item--active {
  background: rgba(var(--v-theme-primary), 0.2) !important;
}

/* Mejora de contraste para placeholders en modo oscuro */
.v-theme--darkTheme .v-field input::placeholder,
.v-theme--darkTheme .v-field textarea::placeholder {
  color: rgba(255, 255, 255, 0.4) !important;
}

/**
 * ===========================================================================
 * CORRECCIÓN DE TEXTOS EN MODO OSCURO
 * ===========================================================================
 * Vuetify usa clases como text-grey, text-grey-darken-3, etc.
 * En modo oscuro, estos colores deben ser más claros para ser legibles.
 */

/* Texto gris genérico - debe ser claro en modo oscuro */
.v-theme--darkTheme .text-grey,
.v-theme--darkTheme .text-medium-emphasis {
  color: rgba(255, 255, 255, 0.7) !important;
}

/* Texto gris oscuro (darken) - en modo oscuro debe ser claro */
.v-theme--darkTheme .text-grey-darken-1,
.v-theme--darkTheme .text-grey-darken-2,
.v-theme--darkTheme .text-grey-darken-3,
.v-theme--darkTheme .text-grey-darken-4 {
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Subtítulos y captions en modo oscuro */
.v-theme--darkTheme .text-caption {
  color: rgba(255, 255, 255, 0.6) !important;
}

/* Títulos en cards y secciones */
.v-theme--darkTheme .v-card-title,
.v-theme--darkTheme .v-card-subtitle {
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* List items text */
.v-theme--darkTheme .v-list-item-title {
  color: rgb(var(--v-theme-on-surface)) !important;
}

.v-theme--darkTheme .v-list-item-subtitle {
  color: rgba(255, 255, 255, 0.6) !important;
}

/* Badges y chips con texto */
.v-theme--darkTheme .v-chip .v-chip__content {
  color: inherit;
}

/* Headers de tablas */
.v-theme--darkTheme .v-data-table-header th {
  color: rgb(var(--v-theme-on-surface)) !important;
  background: rgb(var(--v-theme-surface-variant)) !important;
}

/* Celdas de tablas */
.v-theme--darkTheme .v-data-table tbody td {
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Form labels */
.v-theme--darkTheme .v-label {
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Text fields - texto de input */
.v-theme--darkTheme .v-field__input {
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Iconos en modo oscuro */
.v-theme--darkTheme .v-icon {
  color: rgb(var(--v-theme-on-surface));
}

/* Progress circular text */
.v-theme--darkTheme .v-progress-circular__content {
  color: rgb(var(--v-theme-on-surface)) !important;
}

/**
 * ===========================================================================
 * CORRECCIÓN DE CLASES DE COLOR FIJO EN MODO OSCURO
 * ===========================================================================
 * Vuetify usa clases como bg-grey-lighten-4 que son colores fijos claros.
 * En modo oscuro, estos deben adaptarse a colores oscuros.
 */

/* Fondos grises claros - en modo oscuro deben ser oscuros */
.v-theme--darkTheme .bg-grey-lighten-1,
.v-theme--darkTheme .bg-grey-lighten-2,
.v-theme--darkTheme .bg-grey-lighten-3,
.v-theme--darkTheme .bg-grey-lighten-4,
.v-theme--darkTheme .bg-grey-lighten-5 {
  background-color: rgb(var(--v-theme-surface-variant)) !important;
}

/* Inputs con bg-color="grey-lighten-X" en modo oscuro */
.v-theme--darkTheme .v-field.v-field--variant-filled,
.v-theme--darkTheme .v-input--density-comfortable .v-field--variant-filled {
  background: rgb(var(--v-theme-surface-variant)) !important;
}

/* Fix específico para inputs con bg-color prop */
.v-theme--darkTheme [class*="bg-grey-lighten"] .v-field__field,
.v-theme--darkTheme .v-field[style*="background"] {
  background: rgb(var(--v-theme-surface-variant)) !important;
}

/* Inputs readonly con fondo - en modo oscuro */
.v-theme--darkTheme .v-text-field .v-field--variant-outlined.v-field--focused,
.v-theme--darkTheme .v-text-field .v-field--variant-outlined {
  background: transparent;
}

/* Cuando el input tiene bg-color definido via Vuetify */
.v-theme--darkTheme .v-input .v-field.bg-grey-lighten-4,
.v-theme--darkTheme .v-input .v-field.bg-grey-lighten-5 {
  background-color: rgb(var(--v-theme-surface-variant)) !important;
}

/**
 * ===========================================================================
 * FIX PARA TÍTULOS Y ENCABEZADOS
 * ===========================================================================
 */

/* Títulos h1-h6 en modo oscuro */
.v-theme--darkTheme h1,
.v-theme--darkTheme h2,
.v-theme--darkTheme h3,
.v-theme--darkTheme h4,
.v-theme--darkTheme h5,
.v-theme--darkTheme h6,
.v-theme--darkTheme .text-h1,
.v-theme--darkTheme .text-h2,
.v-theme--darkTheme .text-h3,
.v-theme--darkTheme .text-h4,
.v-theme--darkTheme .text-h5,
.v-theme--darkTheme .text-h6 {
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Botones con variant="text" en modo oscuro - iconos y texto */
.v-theme--darkTheme .v-btn--variant-text {
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Font weight bold text en modo oscuro */
.v-theme--darkTheme .font-weight-bold,
.v-theme--darkTheme .font-weight-medium {
  color: rgb(var(--v-theme-on-surface));
}

/**
 * ===========================================================================
 * CLASES UTILITARIAS PARA TEMAS
 * ===========================================================================
 * Clases que se adaptan automáticamente al tema activo
 */

/* Input readonly - fondo sutil que se adapta al tema */
.input-readonly :deep(.v-field) {
  background-color: rgba(var(--v-theme-on-surface), 0.05) !important;
}

.v-theme--darkTheme .input-readonly :deep(.v-field) {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

/* Card con fondo sutil - se adapta al tema */
.card-subtle {
  background-color: rgba(var(--v-theme-on-surface), 0.03) !important;
}

.v-theme--darkTheme .card-subtle {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

/* Switch en modo oscuro - label visible */
.v-theme--darkTheme .v-switch .v-label {
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Switch - thumb (círculo) blanco */
.v-switch .v-switch__thumb {
  background-color: white !important;
}

/* Switch desactivado - track más visible */
.v-switch:not(.v-input--is-focused) .v-switch__track:not([class*="bg-"]) {
  background-color: #B0BEC5 !important;
  opacity: 1 !important;
}
.v-input--horizontal:not(.v-input--dirty) .v-switch__track {
  background-color: #B0BEC5 !important;
  opacity: 1 !important;
}

/**
 * ===========================================================================
 * ESTILOS PARA STEPPER
 * ===========================================================================
 * Estilos para el stepper de progreso en las vistas de emisión
 */

/**
 * ===========================================================================
 * ESTILOS PARA STEPPER EN TEMA CLARO
 * ===========================================================================
 */

/* Stepper en modo claro */
.v-theme--lightTheme .v-stepper {
  background: rgb(var(--v-theme-surface)) !important;
}

.v-theme--lightTheme .v-stepper-header {
  background: transparent !important;
}

/* Pasos del stepper - modo claro */
.v-theme--lightTheme .v-stepper-item {
  color: rgb(var(--v-theme-on-surface)) !important;
}

.v-theme--lightTheme .v-stepper-item__avatar {
  background: #E0E0E0 !important;
  color: #616161 !important;
}

.v-theme--lightTheme .v-stepper-item--selected .v-stepper-item__avatar {
  background: rgb(var(--v-theme-primary)) !important;
  color: white !important;
}

.v-theme--lightTheme .v-stepper-item--complete .v-stepper-item__avatar {
  background: rgb(var(--v-theme-primary)) !important;
  color: white !important;
}

/* Títulos del stepper - modo claro */
.v-theme--lightTheme .v-stepper-item__subtitle,
.v-theme--lightTheme .v-stepper-item__title {
  color: rgb(var(--v-theme-on-surface)) !important;
}

.v-theme--lightTheme .v-stepper-item--selected .v-stepper-item__title {
  color: rgb(var(--v-theme-primary)) !important;
  font-weight: 600;
}

/**
 * ===========================================================================
 * ESTILOS PARA STEPPER EN TEMA OSCURO
 * ===========================================================================
 */

/* Stepper en modo oscuro */
.v-theme--darkTheme .v-stepper {
  background: rgb(var(--v-theme-surface)) !important;
}

.v-theme--darkTheme .v-stepper-header {
  background: transparent !important;
}

/* Pasos del stepper - modo oscuro */
.v-theme--darkTheme .v-stepper-item {
  color: rgb(var(--v-theme-on-surface)) !important;
}

.v-theme--darkTheme .v-stepper-item__avatar {
  background: rgb(var(--v-theme-surface-variant)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
}

.v-theme--darkTheme .v-stepper-item--selected .v-stepper-item__avatar {
  background: rgb(var(--v-theme-primary)) !important;
  color: white !important;
}

.v-theme--darkTheme .v-stepper-item--complete .v-stepper-item__avatar {
  background: rgb(var(--v-theme-primary)) !important;
  color: white !important;
}

/* Títulos del stepper - modo oscuro */
.v-theme--darkTheme .v-stepper-item__subtitle,
.v-theme--darkTheme .v-stepper-item__title {
  color: rgb(var(--v-theme-on-surface)) !important;
}

.v-theme--darkTheme .v-stepper-item--selected .v-stepper-item__title {
  color: rgb(var(--v-theme-primary)) !important;
  font-weight: 600;
}

/**
 * ===========================================================================
 * ESTILOS GLOBALES PARA TÍTULOS DE PÁGINA Y NAVEGACIÓN
 * ===========================================================================
 * Estos estilos aseguran que los títulos de página y botones de navegación
 * sean visibles en ambos temas (claro y oscuro) en TODAS las vistas.
 */

/* Títulos de página h1 a h6 - siempre visibles según tema */
.page-title,
.text-h1, .text-h2, .text-h3, .text-h4, .text-h5, .text-h6,
h1, h2, h3, h4, h5, h6 {
  color: rgb(var(--v-theme-on-background));
}

/* Subtítulos de página */
.page-subtitle {
  color: rgba(var(--v-theme-on-background), 0.7);
}

/* En modo oscuro - garantizar visibilidad */
.v-theme--darkTheme .page-title,
.v-theme--darkTheme h1.font-weight-bold,
.v-theme--darkTheme h1,
.v-theme--darkTheme h2,
.v-theme--darkTheme h3,
.v-theme--darkTheme h4 {
  color: #FFFFFF !important;
}

.v-theme--darkTheme .page-subtitle,
.v-theme--darkTheme .text-body-2.page-subtitle {
  color: rgba(255, 255, 255, 0.75) !important;
}

/* Botones de navegación (flecha hacia atrás) en modo oscuro */
.v-theme--darkTheme .v-btn--variant-text.v-btn--icon,
.v-theme--darkTheme .v-btn--variant-text .v-icon {
  color: #FFFFFF !important;
  opacity: 1 !important;
}

/* En modo claro - colores oscuros para contraste */
.v-theme--lightTheme .page-title,
.v-theme--lightTheme h1.font-weight-bold,
.v-theme--lightTheme h1,
.v-theme--lightTheme h2,
.v-theme--lightTheme h3,
.v-theme--lightTheme h4 {
  color: #1E1E1E !important;
}

.v-theme--lightTheme .page-subtitle,
.v-theme--lightTheme .text-body-2.page-subtitle {
  color: rgba(0, 0, 0, 0.6) !important;
}

/* Botones de navegación en modo claro */
.v-theme--lightTheme .v-btn--variant-text.v-btn--icon,
.v-theme--lightTheme .v-btn--variant-text .v-icon {
  color: #424242 !important;
  opacity: 1 !important;
}

/* Fix específico para botones icon con variant text */
.v-btn--variant-text {
  opacity: 1 !important;
}

.v-btn--variant-text .v-icon {
  opacity: 1 !important;
}

/**
 * ===========================================================================
 * CLASES DE FONDO AZUL CLARO EN MODO OSCURO
 * ===========================================================================
 * Igual que bg-grey-lighten, las clases bg-blue-lighten deben adaptarse
 */

.v-theme--darkTheme .bg-blue-lighten-1,
.v-theme--darkTheme .bg-blue-lighten-2,
.v-theme--darkTheme .bg-blue-lighten-3,
.v-theme--darkTheme .bg-blue-lighten-4,
.v-theme--darkTheme .bg-blue-lighten-5 {
  background-color: rgba(33, 150, 243, 0.15) !important;
}

/* ============================================================
   ACCESIBILIDAD
   ============================================================ */

/* Skip link: visible solo con teclado (Tab) */
.skip-link {
  position: absolute;
  top: -100%;
  left: 16px;
  z-index: 9999;
  padding: 8px 16px;
  background: rgb(var(--v-theme-primary));
  color: white;
  border-radius: 4px;
  text-decoration: none;
  font-size: 14px;
}

.skip-link:focus {
  top: 16px;
}

/* prefers-reduced-motion: desactiva animaciones para usuarios sensibles.
   NOTA: NO usar selector universal (*) — rompe overlays de Vuetify (dropdowns, menus).
   Solo aplicar a elementos propios de la app, excluyendo componentes de Vuetify. */
@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active,
  .slide-y-transition-enter-active,
  .slide-y-transition-leave-active {
    transition: none !important;
  }

  #nprogress .bar {
    transition: none !important;
  }
}

/* ============================================================
   MODALES ADAPTATIVOS EN MOVIL
   ============================================================ */
@media (max-width: 600px) {
  .v-dialog > .v-overlay__content {
    max-width: 95vw !important;
    margin: 8px !important;
  }

  .v-dialog > .v-overlay__content > .v-card {
    max-height: 90vh;
    overflow-y: auto;
  }
}

/* ============================================================
   FOCUS RING UNIFICADO (accesibilidad teclado)
   ============================================================ */
*:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary)) !important;
  outline-offset: 2px;
}

.v-btn:focus-visible {
  box-shadow: 0 0 0 3px rgba(var(--v-theme-primary), 0.35) !important;
  outline: none !important;
}

/* ============================================================
   HOVER CONSISTENTE EN CARDS INTERACTIVAS
   ============================================================ */
.card-hover {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.card-hover:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
}

.card-hover:active {
  transform: translateY(0);
}

.v-theme--darkTheme .card-hover:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4) !important;
}

/* ============================================================
   SKELETON LOADING (utilidad reutilizable)
   ============================================================ */
.skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
  border-radius: 4px;
}

.v-theme--darkTheme .skeleton {
  background: linear-gradient(90deg, #2a2a2a 25%, #3a3a3a 50%, #2a2a2a 75%);
  background-size: 200% 100%;
}

@keyframes skeleton-pulse {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton-text {
  height: 14px;
  width: 80%;
  margin-bottom: 8px;
}

.skeleton-title {
  height: 24px;
  width: 60%;
  margin-bottom: 12px;
}

.skeleton-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

/* ============================================================
   ANIMACION FADE-IN PARA CONTENIDO DE PAGINA
   ============================================================ */
.page-enter {
  animation: pageEnter 0.3s ease-out;
}

@keyframes pageEnter {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ============================================================
   MEJORAS DE CONTRASTE Y TIPOGRAFIA
   ============================================================ */
.text-grey {
  color: rgba(var(--v-theme-on-surface), 0.6) !important;
}

.v-theme--darkTheme .text-grey {
  color: rgba(255, 255, 255, 0.6) !important;
}
</style>
